import MonetizationOnOutlined from "@mui/icons-material/MonetizationOnOutlined";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { formatCurrency } from "util/currency";
import { prettyPrintDateTime } from "util/dates";

/**
 * Structural row shape (rather than a query-derived type) so any query that
 * selects the payment fields can feed this component — the edit form's full
 * query and the stepper's slimmer step query alike.
 */
type SubmissionPaymentRow = {
  id: string;
  amount?: number | null;
  payment: {
    id: string;
    amount?: number | null;
    payee?: string | null;
    paidAt?: string | null;
    status?: string | null;
  };
};

interface SubmissionPaymentProps {
  submissionPayments: SubmissionPaymentRow[];
  isEditMode: boolean;
  onAddPayment: () => void;
  onOpenPayment: (paymentId: string) => void;
  disabled?: boolean;
}

export function SubmissionPayment({
  submissionPayments,
  isEditMode,
  onAddPayment,
  onOpenPayment,
  disabled = false,
}: SubmissionPaymentProps) {
  const theme = useTheme();

  if (!isEditMode) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          "& .MuiTableCell-root": {
            borderColor: theme.palette.divider,
          },
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.grey50.main,
              }}
            >
              <TableCell sx={{ fontWeight: 500 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>Payee</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>Payment Date</TableCell>
              <TableCell sx={{ fontWeight: 500 }} align="right">
                Amount
              </TableCell>
              <TableCell sx={{ width: 100 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submissionPayments.length > 0 ? (
              <>
                {submissionPayments.map((submissionPayment) => {
                  const displayAmount =
                    submissionPayment.amount ?? submissionPayment.payment.amount;

                  return (
                    <TableRow
                      key={submissionPayment.id}
                      hover={!disabled}
                      sx={{
                        cursor: disabled ? "default" : "pointer",
                        ...(disabled
                          ? {}
                          : {
                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }),
                      }}
                      onClick={
                        disabled ? undefined : () => onOpenPayment(submissionPayment.payment.id)
                      }
                    >
                      <TableCell>
                        {submissionPayment.payment.status ? (
                          <Typography variant="body2" color="text.primary">
                            {prettyPrintEnumValue(submissionPayment.payment.status)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{submissionPayment.payment.payee || "—"}</TableCell>
                      <TableCell>
                        {submissionPayment.payment.paidAt
                          ? prettyPrintDateTime(submissionPayment.payment.paidAt)
                          : "—"}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(displayAmount)}</TableCell>
                      <TableCell sx={{ width: 100, whiteSpace: "nowrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {/* Future: Add edit/delete buttons here */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow
                hover={!disabled}
                sx={{ cursor: disabled ? "default" : "pointer" }}
                onClick={disabled ? undefined : onAddPayment}
              >
                <TableCell colSpan={5}>
                  {disabled ? (
                    <Typography variant="body2" color="text.secondary">
                      No payments
                    </Typography>
                  ) : (
                    <Button
                      startIcon={<MonetizationOnOutlined />}
                      variant="text"
                      color="primary"
                      sx={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      Add Payment
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
